<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  totalPages: number;
}
const model = defineModel<number>({ required: true });

const props = defineProps<Props>();

const isFirstPage = computed(() => model.value <= 1);
const isLastPage = computed(() => model.value >= props.totalPages);

function goTo (page: number): void {
  if (page < 1 || page > props.totalPages) {
    return;
  }
  model.value = page;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

const pages = computed(() => {
  const total = props.totalPages;
  const current = model.value;

  if (total <= 6) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, '...', total];
  }

  if (current >= total - 3) {
    return [1, '...', total - 3, total - 2, total - 1, total];
  }

  return [1, '...', current - 1, current, current + 1, '...', total];
});

const pageClasses = computed(() => {
  return (p: number | string): string => {
    const base = 'w-7 h-7 flex items-center justify-center rounded-full text-sm transition-all duration-200';

    if (typeof p !== 'number') {
      return `${base} text-white/80 cursor-default`;
    }

    const isActive = p === model.value;

    if (isActive) {
      return `${base} bg-white text-red-500 font-bold`;
    }

    return `${base} hover:bg-white/20`;
  };
});
</script>

<template>
  <div
    class="flex items-center gap-3 bg-red-500 w-fit h-fit text-white px-5 py-3 rounded-full select-none"
  >
    <button
      class="text-white/90 hover:text-white text-sm font-semibold flex items-center gap-2 transition-all duration-200 disabled:text-white/60 disabled:bg-inherit"
      :disabled="isFirstPage"
      @click="goTo(model - 1)"
    >
      <hfc-icon v-if="!isFirstPage" icon="chevron-left" class="text-white/80 w-2 h-2" />
      Prev
    </button>

    <div class="flex items-center gap-2">
      <button
        v-for="(p, i) in pages"
        :key="i"
        :class="pageClasses(p)"
        @click="typeof p === 'number' && goTo(p)"
      >
        {{ p }}
      </button>
    </div>

    <button
      class="text-white/90 hover:text-white text-sm font-semibold flex items-center gap-2 transition-all duration-200 disabled:text-white/60 disabled:bg-inherit"
      :disabled="isLastPage"
      @click="goTo(model + 1)"
    >
      Next
      <hfc-icon v-if="!isLastPage" icon="chevron-right" class="text-white/80 w-2 h-2" />
    </button>
  </div>
</template>
